import { yupResolver } from '@hookform/resolvers/yup'
import { AlertCircle } from 'lucide-react'
import { useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { isValidPhoneNumber } from 'react-phone-number-input'
import * as yup from 'yup'

import { PageTitle } from '@/components'
import { Button, DateTimePicker, Input, Modal } from '@/components/ui'
import FormGroup from '@/core/components/FormGroup'
import { CreatClient } from '@/common/models/clients'
import { type IClient, type IUserInfo } from '@/core/services/clients'
import { MessageBox } from '../messages/MessageBox'
import { useClients } from '@/home/hooks/use-clients'
import Loader from '@/common/components/Loader'
import {
  TroubleshootUserDetails,
  type EnteredUserValues,
} from './TroubleshootUserDetails'

export interface IClientsValues {
  firstName: string
  lastName: string
  phoneNumber: string
  email: string
  password: string
  birthDate: string
}

export interface CreateClientModalProps {
  showCreateModal: boolean
  setShowCreateModal: (show: boolean) => void
  page: number
  size: number
  sortBy?: string
  setTotalElemenets: (elements: number) => void
  setCLients: (c: IClient[]) => void
}

export const CreateClientModal = (
  props: CreateClientModalProps,
): JSX.Element => {
  const {
    showCreateModal,
    setShowCreateModal,
    page,
    size,
    sortBy,
    setCLients,
    setTotalElemenets,
  } = props

  const [isError, setIsError] = useState<boolean>(false)
  const [errorStatus, setErrorStatus] = useState<number | undefined>(undefined)
  const [showMessageBox, setShowMessageBox] = useState<boolean>(false)
  const [message, setMessage] = useState<string>('')
  const [troubleshootInfo, setTroubleshootInfo] = useState<IUserInfo | null>(
    null,
  )
  // The values that were submitted, kept so the panel can show them next to the stored records.
  const [enteredValues, setEnteredValues] = useState<EnteredUserValues | null>(
    null,
  )
  const { createClient, fetchClients, troubleshoot, loading } = useClients()

  const schema = yup.object().shape({
    firstName: yup.string().label('First Name').required(),
    lastName: yup.string().label('Last Name').required(),
    phoneNumber: yup
      .string()
      .label('Phone Number')
      .required()
      .test('Valid phone number', 'Invalid phone number', (value) => {
        if (value) return isValidPhoneNumber(value)
        return false
      }),
    email: yup.string().email().label('Email').required(),
    password: yup.string().label('Password').required(),
    birthDate: yup.string().label('Birthdate').required(),
  })

  const {
    handleSubmit,
    register,
    formState: { errors, dirtyFields },
    reset,
    control,
    setError,
  } = useForm<IClientsValues>({
    mode: 'onChange',
    resolver: yupResolver<IClientsValues>(schema),
  })

  const handleClose = () => {
    reset()
    setTroubleshootInfo(null)
    setEnteredValues(null)
    setShowCreateModal(false)
  }

  // on a failed save, pull whatever already exists for this phone/email so support
  // can see which side (IAM or database) the client is already on
  const loadTroubleshootInfo = async (values: IClientsValues) => {
    const phone = values.phoneNumber?.trim()
    const email = values.email?.trim()
    try {
      let info = await troubleshoot({ phone })
      if (!info.keycloakUserInfo && !info.databaseUserInfo && email) {
        info = await troubleshoot({ email })
      }
      setEnteredValues({
        firstName: values.firstName,
        lastName: values.lastName,
        phone,
        email,
      })
      setTroubleshootInfo(info)
    } catch (e) {
      console.error(e)
    }
  }

  const submit = async (values: IClientsValues) => {
    setTroubleshootInfo(null)
    setEnteredValues(null)
    try {
      const data: CreatClient = {
        firstName: values.firstName,
        lastName: values.lastName,
        phoneNumber: values.phoneNumber,
        email: values.email,
        password: values.password,
        birthDate: new Date(values.birthDate).valueOf(),
      }

      await createClient(data)

      const response2 = await fetchClients({
        page,
        size,
        sortBy,
      })

      setCLients(response2.content)
      setTotalElemenets(response2.totalElements)
      setIsError(false)
      setShowMessageBox(true)
      setMessage('Create Successful')
      handleClose()
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      if (e.response?.status === 409) {
        setError('email', {
          type: 'manual',
          message: 'This email address is already registered',
        })
        setError('phoneNumber', {
          type: 'manual',
          message: 'This phone number is already registered',
        })
      } else {
        if (e instanceof Error) {
          setIsError(true)
          setShowMessageBox(true)
          setMessage(e.message)
          setErrorStatus(
            (e as { response?: { status?: number } })?.response?.status,
          )
        } else {
          console.error(e)
        }
      }
      await loadTroubleshootInfo(values)
    }
  }

  return (
    <>
      <Modal
        open={showCreateModal}
        onOpenChange={setShowCreateModal}
        size="lg"
        title={<PageTitle title="Add Client" />}
        footer={
          <div className="flex justify-end gap-2 w-full">
            <Button onClick={handleClose} variant="secondary">
              Cancel
            </Button>
            <Button
              form="create-client-form"
              type="submit"
              disabled={loading || Object.keys(dirtyFields).length === 0}
              loading={loading}
            >
              Save
            </Button>
          </div>
        }
      >
        {loading && <Loader />}
        <form
          id="create-client-form"
          onSubmit={handleSubmit(submit)}
          className="flex flex-col gap-4"
        >
          <FormGroup label="First Name" error={errors?.firstName?.message}>
            <Input
              isInvalid={!!errors?.firstName?.message}
              {...register('firstName')}
              type="text"
              placeholder="Enter First Name"
            />
          </FormGroup>
          <FormGroup label="Last Name" error={errors?.lastName?.message}>
            <Input
              isInvalid={!!errors?.lastName?.message}
              {...register('lastName')}
              type="text"
              placeholder="Enter Last Name"
            />
          </FormGroup>
          <FormGroup label="Phone Number" error={errors?.phoneNumber?.message}>
            <Input
              isInvalid={!!errors?.phoneNumber?.message}
              {...register('phoneNumber')}
              type="text"
              placeholder="Enter Phone Number"
            />
          </FormGroup>
          <FormGroup label="Email" error={errors?.email?.message}>
            <Input
              isInvalid={!!errors?.email?.message}
              {...register('email')}
              type="text"
              placeholder="Enter Email"
            />
          </FormGroup>
          <FormGroup label="Enter Password" error={errors?.password?.message}>
            <Input
              isInvalid={!!errors?.password?.message}
              {...register('password')}
              type="password"
              placeholder="Enter Password"
            />
          </FormGroup>
          <Controller
            name="birthDate"
            control={control}
            render={({ field }) => (
              <FormGroup label="Birthdate" error={errors?.birthDate?.message}>
                <DateTimePicker
                  value={field.value ? new Date(field.value) : null}
                  onChange={(date) => {
                    field.onChange(date ? date.toISOString() : '')
                  }}
                  mode="date"
                  placeholder="Enter Birthdate"
                  hasError={!!errors?.birthDate?.message}
                />
              </FormGroup>
            )}
          />
        </form>

        {troubleshootInfo && (
          <div className="mt-6 flex flex-col gap-3">
            {/* Loud on purpose: support was reading past this and assuming the save worked. */}
            <div className="flex items-start gap-2 rounded-2xl border border-accent-3 bg-accent-3/10 p-4">
              <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-accent-3" />
              <span className="text-body-medium font-semibold text-accent-3">
                The client could not be created. Existing records for this phone
                number or email:
              </span>
            </div>
            <TroubleshootUserDetails
              userInfo={troubleshootInfo}
              entered={enteredValues ?? undefined}
              keyFieldsOnly
              emptyMessage="No existing records found in IAM or in the database for this phone number or email."
            />
          </div>
        )}
      </Modal>
      <MessageBox
        message={message}
        setShowMessageBox={setShowMessageBox}
        showMessageBox={showMessageBox}
        isError={isError}
        errorStatus={errorStatus}
      />
    </>
  )
}
